/**
 * 级联筛选器
 * 统一叫法：左侧叫Group，右侧叫child
 * 目前应用是指标筛选器，左侧是数据源，右侧是指标
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { getIntl } from 'umi';
import { constant, debounce } from 'lodash-es';
import { Dropdown } from 'tea-component';
import CascaderItem from './CascaderItem';
import DisplayEmpty from './DisplayEmpty';
import DisplaySingleValue from './DisplaySingleValue';
import DisplayNormal from './DisplayNormal';
import CascaderSearch from './CascaderSearch';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import { getShowLabel, resolveCascaderCategoryLabel, getCascaderDataSourceKey, getCascaderLeafIdentity } from '@intelligence/components/Form/utils';
import {
  ICascaderProps,
  IChildProp,
  IShortcutItem,
  TCascaderValue,
} from '../../../type';
import './index.scss';

const getLeafItems = (item: IChildProp): IChildProp[] =>
  item.children?.length ? item.children.flatMap(getLeafItems) : [item];

/**
 * 获取初始化选中的列表和扁平化列表
 *
 * @param formItems - 表单项数组，包含父级和子级选项
 * @param formValue - 当前选中的值，格式为对象，键为父级选项的值，值为子级选项的数组
 * @returns 返回一个对象，包含以下属性：
 *  - initedSelectedList: 初始化的选中列表，包含选中的父级和子级项
 *  - initedFlattenList: 扁平化的选中子级项列表
 *  - groupFullCheckedNumberMap: 每个父级选项下子级选项的总数映射
 */
const getInitSelectedList = (
  formItems: IChildProp[] = [],
  formValue: ICascaderProps['value'] = {},
) => {
  const resultArr: IChildProp[] = [];
  const flattenArr: IChildProp[] = [];
  const fullCheckValues: Record<string, number> = {};
  formItems.forEach((parentItem) => {
    const leafItems = parentItem.children?.some((child) => child.children?.length)
      ? getLeafItems(parentItem)
      : parentItem.children || [];
    fullCheckValues[parentItem.value || ''] = leafItems.length;
    Object.keys(formValue || {}).forEach((parentKey) => {
      if (parentKey === parentItem.value) {
        const newParentItem = Object.assign({}, parentItem, { children: [] });
        if (formValue[parentKey]?.length) {
          resultArr.push(newParentItem);
          leafItems.forEach((childItem: IChildProp) => {
            const childKey = childItem.value || '';
            if (formValue[parentKey]?.includes(childKey)) {
              newParentItem.children?.push(childItem);
              flattenArr.push(childItem);
            } else {
              return;
            }
          });
        }
      } else {
        return;
      }
    });
  });
  return {
    initedSelectedList: resultArr,
    initedFlattenList: flattenArr,
    groupFullCheckedNumberMap: fullCheckValues,
  };
};

export const getOptionText = (option?: any) => {
  const { formatMessage } = getIntl();
  const optionLabelTextLocaleKey = option?.labelKey || '';
  const optionLabel = option?.label || '';
  if (!optionLabelTextLocaleKey) return optionLabel;
  try {
    return formatMessage({ id: optionLabelTextLocaleKey });
  } catch (exception) {
    return optionLabel;
  }
};

/**
 * 级联选择器组件
 * 支持多级数据的选择,包含搜索、全选、高亮、快捷选项等功能
 *
 * @param props - 组件属性
 * @param props.options - 可选项数据源
 * @param props.value - 选中的值
 * @param props.field - 字段名
 * @param props.onChange - 选项变化时的回调函数
 * @param props.groupName - 分组名称,默认为'Metrics Category'
 * @param props.itemName - 选项名称,默认为'Metrics'
 * @param props.width - 组件宽度,默认272px
 * @param props.height - 内容区高度,默认325px
 * @param props.groupWidth - 分组列表宽度,默认250px
 * @param props.childWidth - 子项列表宽度,默认290px
 * @param props.hasSelectAll - 是否显示全选功能
 * @param props.hasSearch - 是否显示搜索功能
 * @param props.hasHighlight - 是否启用高亮
 * @param props.hasShortcut - 是否启用快捷选项
 * @param props.shortcutOptions - 快捷选项配置
 * @param props.customInputDisplayRender - 自定义输入框显示渲染函数
 * @param props.footerMsg - 底部提示信息
 * @param props.isUseClose - 是否在关闭时触发onChange
 * @param props.isNoShortcutClear - 是否禁用一键清除功能
 * @param props.restoreAllWhenEmptyOnClose - 关闭时若未选择任何值，恢复为全选
 *
 * @returns 级联选择器组件
 */
const Cascader = (props: ICascaderProps, ref: any) => {
  const {
    options = [],
    value,
    field,
    onChange,
    groupName = 'Metrics Category',
    itemName = 'Metrics',
    width = 272,
    height = 325,
    groupWidth = 250,
    childWidth = 290,
    thirdLevelWidth = 250,
    hasThirdLevel = false,
    childUnavailableText,
    thirdLevelUnavailableText,
    thirdLevelUnavailableTextKey,
    hasSelectAll,
    mainCategory,
    subCategory,
    hasSearch,
    searchPlaceholder,
    hasHighlight,
    hasShortcut,
    displaySearchParentText,
    searchTagAutoWidth,
    searchTagWidth,
    shortcutOptions = [],
    shortcutDefaultValue = '',
    customInputDisplayRender,
    customSearchItemDisplayRender,
    footerMsg,
    footerMsgKey,
    isUseClose,
    isNoShortcutClear,
    restoreAllWhenEmptyOnClose,
  } = props;
  const { formatMessage } = useIntlExtend();
  const mainCategoryLabel = resolveCascaderCategoryLabel(mainCategory, formatMessage);
  const subCategoryLabel = resolveCascaderCategoryLabel(subCategory, formatMessage);
  // 选中的项，用于选中某个Group展开child，显示其子项
  const [selectedItem, setSelectedItem] = useState(options?.[0] || {});
  const [selectedPlatform, setSelectedPlatform] = useState(
    options?.[0]?.children?.find((item) => item.children?.length) || {},
  );
  const platformValue = useMemo(() => {
    const selectedValues = value?.[selectedItem?.value || ''] ?? [];
    const initialValue: Record<string, string[]> = { __leafValues: selectedValues };
    return (selectedItem?.children ?? []).reduce((result, item) => ({
      ...result,
      [item.value || '']: getLeafItems(item).map((leaf) => leaf.value || '').filter((leaf) => selectedValues.includes(leaf)),
    }), initialValue);
  }, [selectedItem, value]);
  const resolvedThirdLevelUnavailableText =
    thirdLevelUnavailableText ||
    (thirdLevelUnavailableTextKey
      ? formatMessage({ id: thirdLevelUnavailableTextKey })
      : '');
  const isThirdLevelVisible =
    hasThirdLevel &&
    (!!selectedPlatform?.children?.length || !!resolvedThirdLevelUnavailableText);
  const [searchKey, setSearchKey] = useState('');
  const { initedSelectedList, initedFlattenList, groupFullCheckedNumberMap } =
    useMemo(() => getInitSelectedList(options, value), [options, value]);
  const [flattenSelectedList, setFlattenSelectedList] =
    useState<IChildProp[]>(initedFlattenList);

  const [filteredList, setFilteredList] = useState<IChildProp[]>(options);
  // 原始值是否改变
  const [isChange, setIsChange] = useState(false);

  useEffect(() => {
    setFlattenSelectedList(initedFlattenList);
  }, [initedFlattenList]);

  /**
   * 检查快捷选项是否被选中
   * @param shortcutValues - 快捷选项值的映射值
   * @param checklist - 选中项列表，包含value属性的对象数组
   * @returns 如果所有快捷选项都被选中则返回true，否则返回false
   * @internal
   */
  const getShortcutChecked = (
    shortcutValues: string[],
    checklist: IChildProp[],
  ) =>
    shortcutValues.every((key) =>
      checklist.find((item) => getCascaderLeafIdentity(item) === key),
    );

  // 给sortcutOptions绑定shortcutMap和isChecked字段
  // shortcutMap是给后续点击的时候用的
  const shortcutFormattedOptions = useMemo(
    () =>
      shortcutOptions.map((item: IShortcutItem) => {
        const flattenChildren: string[] = [];
        const map = new Map(
          Object.entries(item.includes).map(([parentKey, children]) => {
            flattenChildren.push(
              ...children.map((child) => `${parentKey}_${child}` || ''),
            );
            return [parentKey, new Set(children)];
          }),
        );
        return {
          ...item,
          shortcutMap: map,
          flattenChildren,
          isChecked: getShortcutChecked(flattenChildren, flattenSelectedList),
        };
      }),
    [shortcutOptions, flattenSelectedList],
  );

  const selectedItemLabel = useMemo(
    () => getShowLabel(selectedItem, formatMessage),
    [selectedItem, formatMessage],
  );

  const changeValues = (
    changeObj: Record<string, any>,
    flattenChangeArr: IChildProp[],
  ) => {
    // console.log(value, changeObj, flattenChangeArr);
    onChange(field, Object.assign({}, value, changeObj));
    setFlattenSelectedList(flattenChangeArr);
  };

  /**
   * 清除选中的值并触发 onChange 回调。
   * 如果 onChange 是一个函数，则调用它并传入字段名和空数组。
   */
  const handleClear = () => {
    const result: TCascaderValue = {};
    Object.keys(value || {}).forEach((key) => {
      result[key] = [];
    });
    changeValues(result, []);
    restoreAllWhenEmptyOnClose && setIsChange(true);
  };

  const handleAll = () => {
    const result: TCascaderValue = {};
    (options || []).map((item: any) => {
      result[item.value] = [];
      if (item.children?.length > 0) {
        result[item.value] = item.children.map((child: any) => child.value);
      }
    });
    changeValues(result, []);
  };

  /**
   * 点击某个group的回调
   */
  const handleItemClick = (formItem: IChildProp) => {
    if (formItem.parent?.parent) {
      setSelectedPlatform(formItem);
      return;
    }
    if (selectedItem.value === formItem.value) return;
    setSelectedItem(formItem);
    setSelectedPlatform(formItem.children?.find((item) => item.children?.length) || {});
  };

  /**
   * 选项发生变化时的回调
   * @param key 触发的某一项的key名
   * @param formItemChecked 选中还是未选中
   * @param formItem 这一项的配置
   */
  const handleChange = (
    key: string,
    isGroup: boolean,
    isIndeterminate: boolean,
    formItemChecked: boolean,
    formItem: IChildProp,
  ) => {
    setIsChange(true);
    // console.log(isGroup, isIndeterminate, formItemChecked, formItem);
    const { children = [], value: formItemKey = '' } = formItem || {};
    // 如果是group
    if (isGroup) {
      // formItemChecked为true选中所有，为false清空
      const changeChildren = (
        hasThirdLevel ? getLeafItems(formItem) : children || []
      ).filter((child) => !child.isDisable);
      const changeChildrenValues = changeChildren.map((child) => child.value);
      const changeAppendParentKeyChildrenValues = changeChildren.map((child) =>
        getCascaderLeafIdentity(child),
      );
      const rootKey = getCascaderDataSourceKey(formItem) || formItemKey;
      const currentValues = value?.[rootKey] || [];
      if (formItemChecked || (!formItemChecked && isIndeterminate)) {
        changeValues(
          { [rootKey]: formItem.parent?.parent ? Array.from(new Set(currentValues.concat(changeChildrenValues))) : changeChildrenValues },
          flattenSelectedList.concat(changeChildren),
        );
      } else {
        changeValues(
          { [rootKey]: currentValues.filter((item) => !changeChildrenValues.includes(item)) },
          flattenSelectedList.filter(
            (item) =>
              !changeAppendParentKeyChildrenValues.includes(
                getCascaderLeafIdentity(item),
              ),
          ),
        );
      }
      formItem.parent?.parent ? setSelectedPlatform(formItem) : setSelectedItem(formItem);
    } else {
      const parentkey = getCascaderDataSourceKey(formItem);
      const targetKey = formItem.value || '';
      const trueKey = getCascaderLeafIdentity(formItem);
      const targetArr = value?.[parentkey] || [];
      const changeObj = { [parentkey]: targetArr };
      // 选中则将选项压进数组
      if (formItemChecked) {
        targetArr.push(targetKey);
        changeValues(changeObj, flattenSelectedList.concat(formItem));
        // 未选中则使用splice方法根据index删除
      } else {
        const index = targetArr.findIndex((key: string) => key === targetKey);
        index >= 0 && targetArr.splice(index, 1);
        changeValues(
          changeObj,
          flattenSelectedList.filter(
            (item) => getCascaderLeafIdentity(item) !== trueKey,
          ),
        );
      }
    }
  };

  /**
   * 处理标签关闭事件。
   * 当用户关闭一个标签时，更新表单项的值和扁平化选择列表。
   *
   * @param formItem - 被关闭的标签对应的表单项，包含父项和当前项的值。
   */
  const handleTagClose = (formItem: IChildProp) => {
    setSearchKey('');
    handleChange(formItem.value || '', false, false, false, formItem);
    // const parentKey = formItem?.parent?.value || '';
    // const targetKey = formItem.value || '';
    // changeValues({ [parentKey]: value[parentKey]?.filter((key: string) => key !== targetKey) }, flattenSelectedList.filter((item) => item.value !== formItem.value));
  };

  /**
   * 处理快捷选项的点击事件，更新级联选择器的值和已选列表
   * @param isShortcutSelected - 快捷选项是否被选中
   * @param shortcutItem - 快捷选项数据，包含选项映射和包含关系
   *
   * 主要功能：
   * 1. 根据快捷选项的选中状态，更新级联值（添加或移除）
   * 2. 维护已选列表，保持选项顺序
   * 3. 处理父子级关系，确保数据一致性
   *
   * @private
   */
  const handleShortcutItemClick = (shortcutItem: IShortcutItem) => {
    const isShortcutSelected = !shortcutItem.isChecked;
    const shortcutValues = shortcutItem?.includes || {};

    // 处理 cascaderValues
    const newCascaderValues = { ...(value || {}) };
    // const modifiedParents = new Set();
    const shortcutMap = shortcutItem.shortcutMap || new Map([]);

    // 遍历所有 shortcut 涉及的父级
    for (const [parentKey, shortcutChildren] of shortcutMap) {
      const currentChildren = newCascaderValues[parentKey] || [];
      const newChildren: string[] = isShortcutSelected
        ? [...new Set([...currentChildren, ...shortcutChildren])] // 合并去重
        : currentChildren.filter((v) => !shortcutChildren.has(v)); // 过滤移除

      if (newChildren.length !== currentChildren.length) {
        newCascaderValues[parentKey] = newChildren;
        // modifiedParents.add(parentKey);
      }
    }

    // 处理 flattenSelectedList（保持顺序）
    const shortcutItems: IChildProp[] = [];
    // 遍历所有快捷选项的父级
    const existingValues = new Set(
      flattenSelectedList.map((item) => getCascaderLeafIdentity(item)),
    );

    // 生成需要添加的项（仅当 isSelected 为 true 时）
    if (isShortcutSelected) {
      for (const [parentKey, children] of Object.entries(shortcutValues)) {
        const parent = options.find((item) => item.value === parentKey);
        for (const childValue of children) {
          if (!existingValues.has(`${parentKey}_${childValue}`)) {
            const findChild = parent
              ? getLeafItems(parent).find((item) => item.value === childValue)
              : undefined;
            if (findChild) {
              shortcutItems.push(findChild);
            }
          }
        }
      }
    }

    // 生成最终列表
    const newFlattenSelectedList = isShortcutSelected
      ? [...flattenSelectedList, ...shortcutItems] // 追加新增项
      : flattenSelectedList.filter(
          (item) => !shortcutMap.get(getCascaderDataSourceKey(item))?.has(item.value || ''),
        );
    setIsChange(true);
    changeValues(newCascaderValues, newFlattenSelectedList);
  };

  /**
   * 处理全选或取消全选的点击事件。
   *
   * @param selectedItem - 当前选中的子项。
   * @param cascaderLevel - 当前级别，1 表示第一层级。
   * @param selectAllFlag - 是否全选的标志。
   *
   * 当在第一层级点击时，根据 selectAllFlag 决定选中所有子项或清空选项，并更新选中的值。
   * 当在其他层级点击时，更新所有选项的选中状态。
   */
  const handleClickAll = (
    selectedItem: IChildProp,
    isGroup: boolean,
    selectAllFlag: boolean,
    selectAllScope: 'all' | 'currentGroup' = 'all',
  ) => {
    if (!isGroup || selectAllScope === 'currentGroup') {
      const parentDisable = selectedItem?.isDisable || false;
      const unDisabledChildren = (
        hasThirdLevel ? getLeafItems(selectedItem) : selectedItem?.children || []
      ).filter((child) => !parentDisable && !child.isDisable);
      const result = selectAllFlag ? unDisabledChildren || [] : [];
      const changeObj = {
        [selectedItem.value || '']: result?.map((child) => child.value) || [],
      };
      if (selectAllFlag) {
        const selectedIdentitySet = new Set(
          flattenSelectedList.map(item => getCascaderLeafIdentity(item)),
        );
        changeValues(
          changeObj,
          flattenSelectedList.concat(
            result.filter(
              item => !selectedIdentitySet.has(getCascaderLeafIdentity(item)),
            ),
          ),
        );
      } else {
        const flattenResult =
          unDisabledChildren?.map((item) => getCascaderLeafIdentity(item)) || [];
        changeValues(
          changeObj,
          flattenSelectedList.filter(
            (item) => !flattenResult.includes(getCascaderLeafIdentity(item)),
          ),
        );
      }
    } else {
      const resultValues: Record<string, any> = {};
      const results: IChildProp[] = [];
      if (selectAllFlag) {
        filteredList.forEach((item: IChildProp) => {
          const parentDisable = item?.isDisable || false;
          const childrens = (
            hasThirdLevel ? getLeafItems(item) : item?.children || []
          ).filter((child) => !parentDisable && !child.isDisable);
          resultValues[item.value || ''] = childrens?.map(
            (child) => child.value,
          );
          results.push(...childrens);
        });
        changeValues(resultValues, results);
      } else {
        const flattenFilteredKeys: string[] = [];
        // 拿到搜索后的列表Keys
        filteredList.forEach((item: IChildProp) => {
          const parentDisable = item?.isDisable || false;
          const clearableChildren = hasThirdLevel
            ? getLeafItems(item)
            : item.children || [];
          clearableChildren.forEach((child: IChildProp) => {
            if (!parentDisable && !child.isDisable) {
              flattenFilteredKeys.push(
                `${item.value}_${child.value}`,
              );
            }
          });
        });
        // 排除未搜索到的选项
        flattenSelectedList.forEach((item) => {
          const parentKey = getCascaderDataSourceKey(item);
          const find = flattenFilteredKeys.includes(
            getCascaderLeafIdentity(item),
          );
          if (!resultValues[parentKey]?.length) {
            resultValues[parentKey] = [];
          }
          if (!find) {
            results.push(item);
            resultValues[parentKey].push(item.value);
          }
        });
        changeValues(resultValues, results);
      }
    }
  };

  const debounceSearch = useRef(
    debounce((searchKey: string) => {
      const result: IChildProp[] = [];
      const searchKeyLower = searchKey?.toLowerCase();
      if (!searchKeyLower?.trim()) {
        setFilteredList(options);
        // TODO: 当清空搜索词后，默认选中第一个，这个逻辑可能需要优化
        setSelectedItem(options[0]);
        return;
      }
      options?.forEach((parentItem) => {
        const targetParentLabel = getShowLabel(
          parentItem,
          formatMessage,
        )?.toLowerCase();
        const childrenResult: IChildProp[] = [];
        if (parentItem.isShow) {
          parentItem.children?.forEach((childItem: IChildProp) => {
            const targetChildLabel = getShowLabel(
              childItem,
              formatMessage,
            )?.toLowerCase();
            if (targetChildLabel.includes(searchKeyLower) && childItem.isShow) {
              childrenResult.push(childItem);
            }
          });
          if (childrenResult.length) {
            result.push({
              ...parentItem,
              children: childrenResult,
            });
          } else if (targetParentLabel.includes(searchKeyLower)) {
            result.push(parentItem);
          }
        }
      });
      setFilteredList(result);
      const find = result?.find(
        (parentItem) => parentItem.value === selectedItem.value,
      );
      if (find) {
        setSelectedItem(find);
      } else if (!result?.length) {
        setSelectedItem({});
      } else if (!find) {
        setSelectedItem(result[0]);
      }
    }, 200),
  ).current;

  const handleSearch = (searchKey: string) => {
    setSearchKey(searchKey);
    debounceSearch(searchKey);
    // to do 更新是否半选状态
  };

  /**
   * 获取当前选中的分组状态。
   *
   * @param formValues - 表单值，包含每个分组的选中项。
   * @returns {Object} 返回一个对象，包含以下属性：
   *   - isChecked: boolean，表示该分组是否全选。
   *   - num: number，表示全选的分组数量。
   *
   * 逻辑说明：
   * 1. 遍历每个分组的选中项。
   * 2. 如果分组的选中项数量与默认全选值一致，则该分组全选，计数加1。
   * 3. 如果分组没有选中项，则继续下一个分组。
   * 4. 如果分组的选中项数量不为0且不等于全选值，则直接跳出循环，返回半选状态。
   */
  const getGroupCurrentChecked = useCallback(() => {
    let isChecked = false;
    let num = 0;
    // 比较过程：
    // 1、当全部未选中则进入下次循环
    // 2、如果选中且和默认全选的值一致，表示这一个group全选，计数+1
    // 3、如果选中且未全部选中，则直接跳出循环，使用半选逻辑
    for (const key of Object.keys(value || {})) {
      const len = value?.[key]?.length;
      if (groupFullCheckedNumberMap[key] === len) {
        isChecked = true;
        num += 1;
      } else if (len === 0) {
        continue;
      } else {
        isChecked = false;
        break;
      }
    }
    return {
      isChecked,
      num,
    };
  }, [groupFullCheckedNumberMap, value]);

  const getShortcutGroupFullChecked = useCallback(() => {
    const result: IShortcutItem[] = [];
    shortcutFormattedOptions.forEach((shortcutItem: IShortcutItem) => {
      const isEvery = shortcutItem?.flattenChildren?.every(
        (key: string) =>
          !!flattenSelectedList?.find(
            (fItem) => getCascaderLeafIdentity(fItem) === key,
          ),
      );
      if (isEvery) {
        result.push(shortcutItem);
      }
    });
    return result;
  }, [shortcutFormattedOptions, flattenSelectedList]);

  /**
   * 根据表单值计算并返回显示条件。
   *
   * @param formValues - 表单的值，类型为 ICascaderProps['value']。
   * @returns 返回一个对象，包含以下属性：
   *  - isEmpty: 是否为空。
   *  - isOne: 是否只有一个子项。
   *  - isSingleGroupChecked：是否只有一个分组被选中。
   *  - isGroupFullChecked: 是否所有组都已选中。
   *  - isMetricChecked: 是否有度量被选中。
   *  - showItem: 显示的项。
   *  - showNum: 显示的数量。
   */
  const getDisplayCondition = () => {
    if (flattenSelectedList.length === 1) {
      return {
        isEmpty: false,
        isOne: true,
        isSingleGroupChecked: false,
        isGroupFullChecked: false,
        checkedGroup: [],
        isMetricChecked: false,
        isShortcutGroupChecked: false,
        showItem: flattenSelectedList[0],
        showNum: 0,
      };
    }
    if (!flattenSelectedList.length) {
      return {
        isEmpty: true,
        isOne: false,
        isSingleGroupChecked: false,
        isGroupFullChecked: false,
        checkedGroup: [],
        isMetricChecked: false,
        isShortcutGroupChecked: false,
        shortcutCheckedGroup: [],
        showItem: {},
        showNum: 0,
      };
    }
    const { isChecked, num } = getGroupCurrentChecked();
    if (isChecked && num === 1) {
      return {
        isEmpty: false,
        isOne: false,
        isSingleGroupChecked: true,
        checkedGroup: initedSelectedList,
        isGroupFullChecked: true,
        isMetricChecked: false,
        isShortcutGroupChecked: false,
        shortcutCheckedGroup: [],
        showItem: flattenSelectedList[0].parent || [],
        showNum: num,
      };
    }
    if (isChecked) {
      return {
        isEmpty: false,
        isOne: false,
        isSingleGroupChecked: false,
        checkedGroup: initedSelectedList,
        isGroupFullChecked: true,
        isShortcutGroupChecked: false,
        shortcutCheckedGroup: [],
        isMetricChecked: false,
        showItem: {},
        showNum: num,
      };
    }
    const shortcutGroupResult = getShortcutGroupFullChecked();
    if (shortcutGroupResult?.length) {
      return {
        isEmpty: false,
        isOne: false,
        isSingleGroupChecked: false,
        isGroupFullChecked: true,
        checkedGroup: [],
        isShortcutGroupChecked: true,
        shortcutCheckedGroup: shortcutGroupResult,
        isMetricChecked: false,
        showItem: flattenSelectedList,
        showNum: num,
      };
    }
    return {
      isEmpty: false,
      isOne: false,
      isSingleGroupChecked: false,
      isGroupFullChecked: false,
      checkedGroup: [],
      isShortcutGroupChecked: false,
      shortcutCheckedGroup: [],
      isMetricChecked: true,
      showItem: {},
      showNum: flattenSelectedList.length,
    };
  };

  /**
   * 渲染选择器的显示内容，根据不同的条件展示不同的组件。
   *
   * @returns {JSX.Element} 返回一个包含选择内容的 div 元素。
   *
   * 根据传入的值，判断当前的显示条件，并选择性地渲染以下组件：
   * - DisplaySingleValue：当只有一个选项时显示”指标icon+指标名“。
   * - DisplayNormal：当某个数据源中指标被全部选中，且没有存在部分选中的情况，显示Metrics Category;当数据源存在部分选中的情况，显示Metrics。
   * - DisplayEmpty：当没有选中项时显示。
   */
  const displayRender = () => {
    const {
      isOne,
      isSingleGroupChecked,
      isGroupFullChecked,
      isMetricChecked,
      showNum,
      showItem,
      isShortcutGroupChecked,
      shortcutCheckedGroup,
      checkedGroup,
    } = getDisplayCondition();
    if (
      customInputDisplayRender &&
      customInputDisplayRender instanceof Function
    ) {
      return customInputDisplayRender({
        isOne,
        isGroupFullChecked,
        isSingleGroupChecked,
        isShortcutGroupChecked,
        shortcutCheckedGroup,
        checkedGroup,
        isMetricChecked,
        showNum,
        showItem,
        groupName,
        itemName,
        DisplayEmpty,
        DisplayNormal,
        shortcutOptions: shortcutFormattedOptions,
      });
    }
    return (
      <div className="dbi-cascader-select-button">
        {(isOne && <DisplaySingleValue showItem={showItem} />) ||
          (isGroupFullChecked && (
            <DisplayNormal groupName={groupName} showLabel={showNum} />
          )) ||
          (isMetricChecked && (
            <DisplayNormal groupName={itemName} showLabel={showNum} />
          )) || <DisplayEmpty />}
      </div>
    );
  };
  const onOpenChange = (e: any) => {
    if (!isUseClose) return;
    if (isChange) {
      const hasSelectedValue = Object.values(value || {}).some(
        (selectedValues) => Array.isArray(selectedValues) && selectedValues.length > 0,
      );
      const confirmedValue = !e && restoreAllWhenEmptyOnClose && !hasSelectedValue
        ? options.reduce<TCascaderValue>((result, item) => {
          const selectableChildren = (
            hasThirdLevel ? getLeafItems(item) : item.children || []
          ).filter((child) => !item.isDisable && !child.isDisable);
          result[item.value || ''] = selectableChildren.map((child) => child.value || '');
          return result;
        }, {})
        : value;
      onChange(field, confirmedValue, !e);
    }
    setIsChange(false);
  };
  // 传出 ref 方法
  useImperativeHandle(ref, () => ({
    reset: () => {
      // handleAll();
      handleClear();
    },
    getInputData: () => ({
      ...getDisplayCondition(),
      groupName,
      itemName,
      DisplayEmpty,
      DisplayNormal,
      shortcutOptions: shortcutFormattedOptions,
    }),
  }));
  return (
    // isNoShortcutClear
    <Dropdown
      className="dbi-cascader dbi-cascader--secondary"
      clearable={!isNoShortcutClear && !!flattenSelectedList.length}
      button={displayRender()}
      appearance="button"
      clickClose={false}
      onClear={handleClear}
      boxClassName="dbi-cascader-box"
      style={{ width }}
      boxStyle={{
        width:
          groupWidth +
          childWidth +
          (isThirdLevelVisible ? thirdLevelWidth : 0),
      }}
      onOpenChange={onOpenChange}
    >
      {hasSearch && (
        <CascaderSearch
          searchKey={searchKey}
          setSearchKey={handleSearch}
          selectedList={flattenSelectedList}
          displayParentText={!!displaySearchParentText}
          tagAutoWidth={searchTagAutoWidth}
          tagWidth={searchTagWidth}
          searchPlaceholder={searchPlaceholder}
          customDisplayRender={customSearchItemDisplayRender}
          onTagClose={handleTagClose}
          onClear={handleClear}
        />
      )}
      <div className="dbi-cascader-content" style={{ height }}>
        <CascaderItem
          setIsChange={setIsChange}
          options={filteredList}
          value={value}
          searchKey={searchKey}
          isGroup={true}
          width={groupWidth}
          hasSelectAll={hasSelectAll}
          hasHighlight={hasHighlight}
          hasShortcut={hasShortcut}
          category={
            mainCategory === '<self>' ? selectedItemLabel : mainCategoryLabel
          }
          selectedItem={selectedItem}
          onItemClick={handleItemClick}
          onChange={handleChange}
          onClickAll={handleClickAll}
          shortcutOptions={shortcutFormattedOptions}
          shortcutDefaultValue={shortcutDefaultValue}
          onShortcutItemClick={handleShortcutItemClick}
        />
        {childUnavailableText ? (
          <div
            className={'dbi-cascader-content-wrap'}
            style={{ width: `${childWidth}px` }}
          >
            <div className={'dbi-cascader-category-wrap'}>
              <span className={'dbi-cascader-checkbox-title'}>{subCategoryLabel}</span>
            </div>
            <div className={'dbc-region-select-item-none'}>
              {childUnavailableText}
            </div>
          </div>
        ) : (
          <CascaderItem
            setIsChange={setIsChange}
            options={selectedItem?.children || []}
            value={hasThirdLevel ? platformValue : value?.[selectedItem?.value || '']}
            searchKey={searchKey}
            width={childWidth}
            isGroup={hasThirdLevel}
            selectedItem={selectedItem}
            hasSelectAll={hasSelectAll}
            selectAllScope={hasThirdLevel ? 'currentGroup' : 'all'}
            hasHighlight={hasHighlight}
            hasShortcut={false}
            category={subCategory === '<self>' ? selectedItemLabel : subCategoryLabel}
            onItemClick={handleItemClick}
            onChange={handleChange}
            onClickAll={handleClickAll}
          />
        )}
        {isThirdLevelVisible &&
          (selectedPlatform?.children?.length ? (
            <CascaderItem
              setIsChange={setIsChange}
              options={selectedPlatform.children}
              value={value?.[selectedItem?.value || ''] || []}
              width={thirdLevelWidth}
              isGroup={false}
              selectedItem={selectedPlatform}
              hasSelectAll={false}
              category={formatMessage({ id: 'dbi_device' })}
              onChange={handleChange}
              onClickAll={handleClickAll}
            />
          ) : (
            <div
              className='dbi-cascader-content-wrap'
              style={{ width: `${thirdLevelWidth}px` }}
            >
              <div className='dbi-cascader-category-wrap'>
                <span className='dbi-cascader-checkbox-title'>
                  {formatMessage({ id: 'dbi_device' })}
                </span>
              </div>
              <div className='dbc-region-select-item-none'>
                {resolvedThirdLevelUnavailableText}
              </div>
            </div>
          ))}
      </div>
      <div className="dbi-cascader-footer">
        {(footerMsg || footerMsgKey) && (
          <div className="dbi-cascader-footer-msg">
            {footerMsgKey
              ? getShowLabel({ footerMsgKey }, formatMessage, 'footerMsg')
              : footerMsg || ''}
          </div>
        )}
      </div>
    </Dropdown>
  );
};
export default forwardRef(Cascader);
